import SearchIcon from "@mui/icons-material/Search"
import FilterListIcon from "@mui/icons-material/FilterList"
import Container from "@mui/material/Container"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Chip from "@mui/material/Chip"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Paper from "@mui/material/Paper"
import Divider from "@mui/material/Divider"
import { styled } from "@mui/material/styles"
import { Index } from "elasticlunr"
import { Link } from "gatsby"
import React, { useState, useEffect, useMemo } from "react"
import * as shortid from "shortid"

const PREFIX = "EnhancedSearchResults"

const classes = {
  searchContainer: `${PREFIX}-searchContainer`,
  filterContainer: `${PREFIX}-filterContainer`,
  categoryChip: `${PREFIX}-categoryChip`,
  resultsContainer: `${PREFIX}-resultsContainer`,
  categorySection: `${PREFIX}-categorySection`,
  categoryTitle: `${PREFIX}-categoryTitle`,
  resultsList: `${PREFIX}-resultsList`,
  resultItem: `${PREFIX}-resultItem`,
  resultLink: `${PREFIX}-resultLink`,
  resultTitle: `${PREFIX}-resultTitle`,
  resultMeta: `${PREFIX}-resultMeta`,
  noResults: `${PREFIX}-noResults`,
  searchStats: `${PREFIX}-searchStats`,
  hideOnMobile: `${PREFIX}-mobileHide`,
}

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.hideOnMobile}`]: {
    [theme.breakpoints.down("sm")]: {
      display: "none"
    },
  },
  [`& .${classes.searchContainer}`]: {
    marginBottom: theme.spacing(3),
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 10,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    boxShadow: "none",
  },

  [`& .${classes.filterContainer}`]: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.categoryChip}`]: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
    },
  },

  [`& .${classes.resultsContainer}`]: {
    maxHeight: "60vh",
    overflowY: "auto",
  },

  [`& .${classes.categorySection}`]: {
    marginBottom: theme.spacing(3),
  },

  [`& .${classes.categoryTitle}`]: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },

  [`& .${classes.resultsList}`]: {
    padding: 0,
    margin: 0,
    listStyle: "none",
  },

  [`& .${classes.resultItem}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.resultLink}`]: {
    display: "block",
    padding: theme.spacing(2),
    textDecoration: "none",
    color: "inherit",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    backgroundColor: "#fff",

    "&:hover": {
      backgroundColor: "#f5f5f5",
      borderColor: theme.palette.primary.main,
      textDecoration: "none",
      color: "inherit",
    },
  },

  [`& .${classes.resultTitle}`]: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },

  [`& .${classes.resultMeta}`]: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem", // exemplu: mai mic pe mobil
      flexDirection: "column", // exemplu: elementele pe verticală
      alignItems: "flex-start",
    },
  },

  [`& .${classes.noResults}`]: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },

  [`& .${classes.searchStats}`]: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(2),
  },
}))

// Category configuration with priorities and Romanian labels
const CATEGORY_CONFIG = {
  procedure: {
    label: "Proceduri",
    priority: 1,
    color: "#1976d2",
    urlPattern: "/proceduri/",
  },
  condition: {
    label: "Afecțiuni",
    priority: 2,
    color: "#388e3c",
    urlPattern: "/afectiuni/",
  },
  news: {
    label: "Noutăți",
    priority: 3,
    color: "#f57c00",
    urlPattern: "/noutati/",
  },
  team: {
    label: "Echipă",
    priority: 4,
    color: "#7b1fa2",
    urlPattern: "/echipa/",
  },
  guide: {
    label: "Ghiduri",
    priority: 5,
    color: "#d32f2f",
    urlPattern: "/ghidul-pacientului/",
  },
  helper: {
    label: "Consilier Medical",
    priority: 6,
    color: "#303f9f",
    urlPattern: "/helpers",
  }
}

const getCategoryFromSlug = (slug) => {
  for (const [key, config] of Object.entries(CATEGORY_CONFIG)) {
    if (slug.includes(config.urlPattern)) {
      return key
    }
  }
  return "Alte rezultate"
}

const EnhancedSearchResults = ({ searchIndex, lang, onResultClick }) => {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [index, setIndex] = useState()
  const [activeFilters, setActiveFilters] = useState(new Set())
  const [isSearching, setIsSearching] = useState(false)

  const getOrCreateIndex = () => index || Index.load(searchIndex)

  // Filter available categories based on current language
  const availableCategories = useMemo(() => {
    return Object.keys(CATEGORY_CONFIG).filter(category => {
      // You can add language-specific filtering here if needed
      return true
    })
  }, [lang])

  const onSearch = async (evt) => {
    const q = evt.target.value
    setQuery(q)

    if (!q.trim()) {
      setResults([])
      return
    }

    setIsSearching(true)

    try {
      const idx = getOrCreateIndex()

      // Enhanced search with multiple strategies
      const searchStrategies = [
        // Exact phrase search
        () => idx.search(`"${q}"`, { expand: false }),
        // Fuzzy search with expansion
        () => idx.search(q, { expand: true }),
        // Individual word search
        () => q.split(' ').flatMap(term =>
          idx.search(term, { expand: true })
        ),
      ]

      let combinedResults = []
      const seenRefs = new Set()

      // Execute search strategies and combine results
      for (const strategy of searchStrategies) {
        try {
          const strategyResults = strategy()
          strategyResults.forEach(result => {
            if (!seenRefs.has(result.ref)) {
              seenRefs.add(result.ref)
              combinedResults.push({
                ...result,
                doc: idx.documentStore.getDoc(result.ref)
              })
            }
          })
        } catch (error) {
          console.warn('Search strategy failed:', error)
        }
      }

      // Sort by relevance score
      combinedResults.sort((a, b) => b.score - a.score)

      // Add manual result for "tarife" or "preturi"
      const normalizedQuery = q.toLowerCase()
      const isMatch = (keyword) => normalizedQuery.length >= 3 && keyword.includes(normalizedQuery);

      if (isMatch("tarife") || isMatch("preturi") || normalizedQuery.includes("tarife") || normalizedQuery.includes("preturi")) {
        const tariffResult = {
          ref: "manual-tarife",
          score: 100, // High score to ensure it appears at top
          doc: {
            id: "manual-tarife",
            title: "Tarife și Prețuri",
            description: "Vezi lista completă de prețuri și tarife pentru serviciile noastre.",
            slug: "/tarife/",
            // attributes required by processing
          }
        }
        combinedResults.unshift(tariffResult)
      }

      // Map to documents and add category information
      const processedResults = combinedResults.map(({ doc }) => ({
        ...doc,
        category: getCategoryFromSlug(doc.slug || ''),
        categoryConfig: CATEGORY_CONFIG[getCategoryFromSlug(doc.slug || '')] || CATEGORY_CONFIG.other
      }))

      setResults(processedResults)
      setIndex(idx)
    } catch (error) {
      console.error('Search error:', error)
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const toggleFilter = (category) => {
    const newFilters = new Set(activeFilters)
    if (newFilters.has(category)) {
      newFilters.delete(category)
    } else {
      newFilters.add(category)
    }
    setActiveFilters(newFilters)
  }

  const clearAllFilters = () => {
    setActiveFilters(new Set())
  }

  // Filter and group results
  const filteredAndGroupedResults = useMemo(() => {
    let filtered = results

    // Apply category filters
    if (activeFilters.size > 0) {
      filtered = results.filter(result => activeFilters.has(result.category))
    }
    // take care of cardiologie interventionala
    /* const removedExceptions = filtered.map(item => {
    if (item.slug.includes('cardiologie-interventionala')) {
      return { ...item, slug: '/cardiologie/#cardiologie-interventionala' };
    }*/

    /* if (item.slug.includes('cardiologie-clinica')) {
      return { ...item, slug: '/cardiologie/#cardiologie-clinica' };
    }

      return item;
    }); */

    // Remove en links
    filtered = filtered.filter(item => !item.slug.includes('en/'));

    // Group by category and sort by priority
    const grouped = filtered.reduce((acc, result) => {
      const category = result.category
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(result)
      return acc
    }, {})

    // Sort categories by priority and limit results per category
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      const priorityA = CATEGORY_CONFIG[a]?.priority || 999
      const priorityB = CATEGORY_CONFIG[b]?.priority || 999
      return priorityA - priorityB
    })

    const result = {}
    sortedCategories.forEach(category => {
      // Limit to top 5 results per category for better UX
      result[category] = grouped[category].slice(0, 5)
    })

    return result
  }, [results, activeFilters, lang])

  const totalResults = Object.values(filteredAndGroupedResults).reduce(
    (sum, categoryResults) => sum + categoryResults.length, 0
  )

  return (
    <StyledContainer maxWidth="md">
      <Paper className={classes.searchContainer} elevation={1}>
        <TextField
          variant="outlined"
          type="text"
          label="Găsește informații medicale și de sănătate"
          placeholder="Introduceți un termen pentru căutare..."
          fullWidth
          value={query}
          autoFocus
          onChange={onSearch}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  color="primary"
                  aria-label="search"
                  size="large"
                  disabled={isSearching}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {query && (
          <Box className={classes.filterContainer}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <FilterListIcon color="action" fontSize="small" />
              <Typography variant="body2" color="textSecondary">
                Filtrează după categorie:
              </Typography>
            </Box>

            <Chip
              label="Toate"
              onClick={clearAllFilters}
              className={classes.categoryChip}
              variant={activeFilters.size === 0 ? "filled" : "outlined"}
              color={activeFilters.size === 0 ? "primary" : "default"}
            />

            {availableCategories.map(category => (
              <Chip
                key={category}
                label={`${CATEGORY_CONFIG[category]?.label || category}`}
                onClick={() => toggleFilter(category)}
                className={`${classes.categoryChip} ${activeFilters.has(category) ? 'active' : ''}`}
                variant={activeFilters.has(category) ? "filled" : "outlined"}
                color={activeFilters.has(category) ? "primary" : "default"}
              />
            ))}
          </Box>
        )}
      </Paper>

      {query && (
        <Box className={classes.searchStats}>
          {isSearching ? (
            <Typography>Se caută...</Typography>
          ) : (
            <Typography>
              {totalResults > 0
                ? `${totalResults} rezultate găsite pentru "${query}"`
                : `Nu s-au găsit rezultate pentru "${query}"`
              }
            </Typography>
          )}
        </Box>
      )}

      <div className={classes.resultsContainer}>
        {totalResults === 0 && query && !isSearching ? (
          <Box className={classes.noResults}>
            <Typography variant="h6" gutterBottom>
              Nu s-au găsit rezultate
            </Typography>
            <Typography variant="body2">
              Încercați cu alți termeni de căutare sau eliminați filtrele active.
            </Typography>
          </Box>
        ) : (
          Object.entries(filteredAndGroupedResults).map(([category, categoryResults]) => (
            <div key={category} className={classes.categorySection}>
              <Typography className={classes.categoryTitle}>

                {CATEGORY_CONFIG[category]?.label || category}
                <Typography component="span" variant="body2" color="textSecondary">
                  ({categoryResults.length})
                </Typography>
              </Typography>

              <ul className={classes.resultsList}>
                {categoryResults.map(page => (
                  <li key={shortid.generate()} className={classes.resultItem}>
                    <Link
                      onClick={() => onResultClick()}
                      to={page.slug}
                      className={classes.resultLink}
                    >
                      <div className={classes.resultTitle}>
                        {page.title}
                      </div>
                      <div className={classes.resultMeta}>
                        <span>{CATEGORY_CONFIG[category]?.label || category}</span>
                        {page.description && (
                          <>
                            <span className={classes.hideOnMobile}>•</span>
                            <span>{page.description.substring(0, 100)}...</span>
                          </>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>

              {Object.keys(filteredAndGroupedResults).length > 1 && (
                <Divider sx={{ mt: 2, mb: 1 }} />
              )}
            </div>
          ))
        )}
      </div>
    </StyledContainer>
  )
}

export default EnhancedSearchResults